/**
 * Canonical Shopify metafield-definition specs — the shared single source
 * for every purpose's definition shape (Feature A: one-click "Create
 * metafield definitions in Shopify" onboarding).
 *
 * Pure type/data module (imports only metafield-purposes and the platen
 * lists in rip-routing, the ONE home of both DTG lists). Consumers:
 *   - The backend `createMetafieldDefinitions` service (Settings →
 *     Organization → Product data lookup) — creates missing definitions on
 *     the org's live store and skip-if-exists-fills the IMS mappings.
 *   - `scripts/create-metafield-definitions.mts` remains the CLI-gated bulk
 *     path; the 9 variant specs below are lifted VERBATIM from it (names,
 *     types, choices, descriptions).
 *
 * Owner type is DERIVED via `deriveOwnerType(purpose)` from the shared
 * PRODUCT_SCOPED_PURPOSES set — never hardcoded. (The old CLI hardcoded
 * PRODUCTVARIANT for its whole set, which was only accidentally right
 * because it never managed the two product-scoped design-ID purposes.)
 */
import { PRODUCT_SCOPED_PURPOSES, } from './metafield-purposes.js';
import { DTG_PLATEN_CODES, PLATEN_VARIANTS } from './rip-routing.js';
/**
 * Derive the Shopify ownerType for a purpose from the shared scope set —
 * the single derivation both the DB CHECK (`scope`) and this module's
 * consumers agree on. Never hardcode PRODUCT/PRODUCTVARIANT per purpose.
 */
export function deriveOwnerType(purpose) {
    return PRODUCT_SCOPED_PURPOSES.has(purpose) ? 'PRODUCT' : 'PRODUCTVARIANT';
}
/**
 * One spec per purpose, over the FULL union — adding a purpose to the shared
 * tuple fails typecheck here until its spec is authored.
 *
 * The 9 variant specs (front/back shape, front/back variant, front/back
 * platen, fulfillment_type, pretreat, custom_print_sku) are lifted verbatim
 * from scripts/create-metafield-definitions.mts.
 */
export const METAFIELD_DEFINITION_SPECS = {
    design_id: {
        purpose: 'design_id',
        key: 'design_id',
        name: 'Design ID',
        type: 'single_line_text_field',
        choices: null,
        description: 'IMS print-file composition: the FRONT design ID (e.g. SAN14M) the per-side filename is composed from. Product-scoped.',
    },
    back_design_id: {
        purpose: 'back_design_id',
        key: 'back_design_id',
        name: 'Back Design ID',
        type: 'single_line_text_field',
        choices: null,
        description: 'IMS print-file composition: the BACK design ID for dual-print products. Product-scoped.',
    },
    design_variant: {
        purpose: 'design_variant',
        key: 'design_variant',
        name: 'Design Variant',
        type: 'single_line_text_field',
        choices: null, // app-side mapper is the allowlist; "None" override must stay writable
        description: 'IMS print-file composition: shared colour variant of the design (Light/Dark/Medium; any other value, e.g. None, composes no suffix).',
    },
    front_design_shape: {
        purpose: 'front_design_shape',
        key: 'front_design_shape',
        name: 'Front Design Shape',
        type: 'single_line_text_field',
        choices: ['Rectangular', 'Square', 'Horizontal', 'Circle'],
        description: 'IMS print-file composition: Design Shape of the FRONT print. Resolved per product type + size to the filename shape code (e.g. Horizontal + Oversized Tee XL -> H6).',
    },
    back_design_shape: {
        purpose: 'back_design_shape',
        key: 'back_design_shape',
        name: 'Back Design Shape',
        type: 'single_line_text_field',
        choices: ['Rectangular', 'Square', 'Horizontal', 'Circle'],
        description: 'IMS print-file composition: Design Shape of the BACK print. Resolved per product type + size to the filename shape code.',
    },
    front_design_variant: {
        purpose: 'front_design_variant',
        key: 'front_design_variant',
        name: 'Front Design Variant',
        type: 'single_line_text_field',
        choices: null, // app-side mapper is the allowlist; "None" override must stay writable
        description: 'IMS print-file composition: per-side override of design_variant for the FRONT print (Light/Dark/Medium; any other value, e.g. None, composes no suffix).',
    },
    back_design_variant: {
        purpose: 'back_design_variant',
        key: 'back_design_variant',
        name: 'Back Design Variant',
        type: 'single_line_text_field',
        choices: null,
        description: 'IMS print-file composition: per-side override of design_variant for the BACK print (Light/Dark/Medium; any other value, e.g. None, composes no suffix).',
    },
    front_design_platen: {
        purpose: 'front_design_platen',
        key: 'front_design_platen',
        name: 'Front Design Platen',
        type: 'single_line_text_field',
        // Phase 86 D-06: the one DTG platen list (same three values as before).
        choices: [...DTG_PLATEN_CODES],
        description: 'IMS print metadata: platen size for the FRONT print (store-only).',
    },
    back_design_platen: {
        purpose: 'back_design_platen',
        key: 'back_design_platen',
        name: 'Back Design Platen',
        type: 'single_line_text_field',
        choices: [...DTG_PLATEN_CODES],
        description: 'IMS print metadata: platen size for the BACK print (store-only).',
    },
    front_design_platen_variant: {
        purpose: 'front_design_platen_variant',
        key: 'front_design_platen_variant',
        name: 'Front Design Platen Variant',
        type: 'single_line_text_field',
        // Phase 86 D-05: the one platen variant list (Small, Large).
        choices: [...PLATEN_VARIANTS],
        description: 'IMS print info: platen variant (Small or Large) for the FRONT print. Filled in by Print Run from the Platen size rules when artwork is uploaded; never typed by hand.',
    },
    back_design_platen_variant: {
        purpose: 'back_design_platen_variant',
        key: 'back_design_platen_variant',
        name: 'Back Design Platen Variant',
        type: 'single_line_text_field',
        choices: [...PLATEN_VARIANTS],
        description: 'IMS print info: platen variant (Small or Large) for the BACK print. Filled in by Print Run from the Platen size rules when artwork is uploaded; never typed by hand.',
    },
    fulfillment_type: {
        purpose: 'fulfillment_type',
        key: 'fulfillment_type',
        name: 'Fulfillment Type',
        type: 'single_line_text_field',
        choices: ['pod', 'pap', 'blank'],
        description: 'IMS fulfillment routing: pod (print on demand), pap (pick & pack), or blank (base garment).',
    },
    pretreat: {
        purpose: 'pretreat',
        key: 'pretreat',
        name: 'Pretreat',
        type: 'boolean',
        choices: null, // boolean type — choices validation not applicable
        description: 'IMS print metadata: whether the garment requires pretreat before printing.',
    },
    custom_print_sku: {
        purpose: 'custom_print_sku',
        key: 'custom_print_sku',
        name: 'Custom Print SKU',
        type: 'single_line_text_field',
        choices: null,
        description: 'IMS routing override: the conforming CUST-… print SKU for this variant when its native Shopify SKU does not match the print grammar. Hand-entered at onboarding; read by order-sync intake, not the print-file composition.',
    },
    front_artwork: {
        purpose: 'front_artwork',
        key: 'front_artwork',
        name: 'Front Artwork',
        type: 'single_line_text_field',
        choices: null,
        description: 'IMS variant artwork: the FRONT print-file storage key for this variant (bare filename, e.g. my-design-front.png). Read by the IMS variant-artwork sync into print_file_lookups; not a design ID and not a URL.',
    },
    back_artwork: {
        purpose: 'back_artwork',
        key: 'back_artwork',
        name: 'Back Artwork',
        type: 'single_line_text_field',
        choices: null,
        description: 'IMS variant artwork: the BACK print-file storage key for this variant (bare filename). Read by the IMS variant-artwork sync into print_file_lookups; not a design ID and not a URL.',
    },
    print_technology: {
        purpose: 'print_technology',
        key: 'print_technology',
        name: 'Print Technology',
        type: 'single_line_text_field',
        choices: ['DTG', 'DTF'],
        description: 'IMS print routing: DTG or DTF for this PRODUCT. When mapped, this is the authoritative source. It sets mirrors onto every POD variant; empty clears back to the location/method-rule default.',
    },
    front_mockup_preview: {
        purpose: 'front_mockup_preview',
        key: 'front_design_preview',
        name: 'Front Mockup Image',
        type: 'file_reference',
        choices: null, // file_reference type — choices validation not applicable
        description: 'IMS packing preview: the FRONT garment mockup image for this variant, shown to packers so they can see how the finished garment should look. Variant-scoped. Display only: never a print file and never sent to a printer.',
    },
    back_mockup_preview: {
        purpose: 'back_mockup_preview',
        key: 'back_design_preview',
        name: 'Back Mockup Image',
        type: 'file_reference',
        choices: null, // file_reference type — choices validation not applicable
        description: 'IMS packing preview: the BACK garment mockup image for this variant. Variant-scoped. Display only: never a print file and never sent to a printer.',
    },
};
/**
 * Feature A's DEFAULT (narrow) onboarding preset — the purposes the
 * one-click "Create metafield definitions" button provisions when the
 * operator doesn't override. Deliberately EXCLUDES every design-ID /
 * design_* composition purpose: provisioning `design_id` for an
 * artwork-driven org would arm the composition sweep AND disarm the
 * variant-artwork leg (which no-ops for orgs with a design_id mapping) —
 * the three-way single-writer invariant is enforced structurally by this
 * exclusion.
 */
// NOTE (BLU-676 review): print_technology is DELIBERATELY absent from BOTH
// presets. The one-click "Create metafield definitions" button auto-FILLS
// mappings, and a print_technology mapping arms the authoritative LIVE
// mirror — whose first sweep over freshly-created (empty) metafields would
// mass-clear manually-set skus.print_technology. Opt-in is EXPLICITLY
// configuring the mapping in the Product data lookup card, never a preset.
//
// NOTE (BLU-1018): front_mockup_preview / back_mockup_preview are absent
// from BOTH presets for a different reason. Every store that has mockups
// already has them, under a field name its own team chose years ago;
// creating a FRESH empty `custom.front_design_preview` definition and
// auto-filling the mapping to it would point Packing at a field nobody
// populates, which looks exactly like "the feature is broken". Pointing a
// mockup purpose at a LIVE store field is an explicit operator choice in
// the Product data lookup card, never a preset.
//
// The presets still exclude them for every existing org. The only exception
// is a one-click run under the `printrun` namespace: a new org set up through
// the wizard, which has no mockup field of its own yet, so the client uploads
// its mockups into the created `printrun.front_preview` / `back_preview`
// fields (Brett, 2026-10-14; `resolveDefinitionsPreset`).
export const ARTWORK_ORG_ONBOARDING_PURPOSES = [
    'front_artwork',
    'back_artwork',
    'custom_print_sku',
    'fulfillment_type',
    'pretreat',
];
/**
 * The COMPOSITION-org preset — for orgs that already map `design_id` (i.e.
 * their print files come from the design-ID composition sweep). Exactly the
 * 9 definitions the CLI (`scripts/create-metafield-definitions.mts`)
 * manages: the six side-specific composition inputs plus fulfilment type,
 * pretreat, and the custom print SKU routing override. Deliberately
 * EXCLUDES:
 *   - `design_id`/`back_design_id`/`design_variant` — a composition org has
 *     those fields already (that's what makes it one), often informal under
 *     a namespace the operator chose years ago; creating formal definitions
 *     at a different namespace would split writes.
 *   - `front_artwork`/`back_artwork` — NOT dead config any more (BLU-838:
 *     the composition sweep mirrors `https://` values for design-ID orgs),
 *     but a composition org usually points these at an EXISTING metafield
 *     (e.g. ThreadHeads' `custom.swiftpod_front_design_id`) rather than a
 *     fresh Print Run definition, so they stay out of the onboarding set.
 */
export const COMPOSITION_ORG_ONBOARDING_PURPOSES = [
    'front_design_shape',
    'back_design_shape',
    'front_design_variant',
    'back_design_variant',
    'front_design_platen',
    'back_design_platen',
    'fulfillment_type',
    'pretreat',
    'custom_print_sku',
];
/**
 * The four platen INFO fields the artwork commit writes on every variant it
 * touches (Phase 86 D-17): platen and platen variant, front and back. Values
 * come from the org's Platen size rules (or a per-product override); nobody
 * types them, and print time never reads them back.
 *
 * Provisioned ONLY by an explicit admin action that passes this list as the
 * `purposes` override to `createMetafieldDefinitions` (D-23; 86-01 checkpoint
 * item 9). It is deliberately NOT part of either onboarding preset: adding
 * purposes to a preset would change what the one-click button does for orgs
 * that are already onboarded.
 */
export const PLATEN_INFO_PURPOSES = [
    'front_design_platen',
    'back_design_platen',
    'front_design_platen_variant',
    'back_design_platen_variant',
];
/**
 * Context-aware preset resolution (shared by the backend service and the
 * Settings UI so the confirmation dialog previews exactly what the server
 * will do): an org that already maps a design-ID purpose is a COMPOSITION
 * org and gets the composition preset; everyone else gets the artwork
 * preset. By construction neither preset can arm the other leg — the
 * three-way single-writer invariant holds without the operator choosing.
 */
export function resolveOnboardingPreset(mappedPurposes) {
    for (const purpose of mappedPurposes) {
        if (purpose === 'design_id' || purpose === 'back_design_id') {
            return COMPOSITION_ORG_ONBOARDING_PURPOSES;
        }
    }
    return ARTWORK_ORG_ONBOARDING_PURPOSES;
}
/**
 * PrintRun's own metafield namespace (D11): the namespace the onboarding
 * wizard sends for a new org with no mappings. A one-click run under it also
 * provisions the two preview image fields (BLU-1399, PV-1).
 */
export const PRINTRUN_METAFIELD_NAMESPACE = 'printrun';
/** The two mockup preview purposes a `printrun` run adds, front then back. */
export const PRINTRUN_PREVIEW_PURPOSES = [
    'front_mockup_preview',
    'back_mockup_preview',
];
/**
 * What a `printrun` run CREATES for a new org with neither preview mapped:
 * the artwork preset, then the front and back preview images (PV-1). A new
 * org has no mockup field of its own, so the BLU-1018 reason for keeping the
 * mockup purposes out of the presets does not apply to it. The onboarding
 * status never REQUIRES them (`resolveOnboardingPreset` is unchanged, PV-4).
 */
export const PRINTRUN_ORG_ONBOARDING_PURPOSES = [
    ...ARTWORK_ORG_ONBOARDING_PURPOSES,
    ...PRINTRUN_PREVIEW_PURPOSES,
];
/** Under `printrun` only: the PrintRun keys, names and an images-only validation for the two previews (PV-3). */
const PRINTRUN_NAMESPACE_SPEC_OVERRIDES = {
    front_mockup_preview: {
        key: 'front_preview',
        name: 'Front preview',
        description: 'PrintRun preview: upload the FRONT mockup image of this ready-made product. The packing team sees it. Display only, never printed.',
        fileTypes: ['Image'],
    },
    back_mockup_preview: {
        key: 'back_preview',
        name: 'Back preview',
        description: 'PrintRun preview: upload the BACK mockup image of this ready-made product. The packing team sees it. Display only, never printed.',
        fileTypes: ['Image'],
    },
};
/**
 * The definition spec a run creates for `purpose` at `namespace`: the base
 * spec OBJECT itself, unless the namespace is `printrun` and the purpose has
 * an override, then a new object with the override over the base (type,
 * owner, purpose and choices stay the base's).
 */
export function resolveDefinitionSpec(purpose, namespace) {
    const base = METAFIELD_DEFINITION_SPECS[purpose];
    if (namespace !== PRINTRUN_METAFIELD_NAMESPACE)
        return base;
    const override = PRINTRUN_NAMESPACE_SPEC_OVERRIDES[purpose];
    return override ? { ...base, ...override } : base;
}
/**
 * What the one-click run CREATES (shared by the backend service and the
 * Settings / wizard dialog): `resolveOnboardingPreset`, plus, for an artwork
 * org under `printrun`, each preview purpose the org has not mapped yet
 * (architect S6: a purpose already mapped, under any namespace, is never
 * probed or created again). `resolveOnboardingPreset` stays what the step
 * REQUIRES.
 */
export function resolveDefinitionsPreset(mappedPurposes, namespace) {
    const mapped = new Set(mappedPurposes);
    const preset = resolveOnboardingPreset(mapped);
    if (preset !== ARTWORK_ORG_ONBOARDING_PURPOSES || namespace !== PRINTRUN_METAFIELD_NAMESPACE) {
        return preset;
    }
    const missing = PRINTRUN_PREVIEW_PURPOSES.filter((purpose) => !mapped.has(purpose));
    if (missing.length === PRINTRUN_PREVIEW_PURPOSES.length)
        return PRINTRUN_ORG_ONBOARDING_PURPOSES;
    if (missing.length === 0)
        return ARTWORK_ORG_ONBOARDING_PURPOSES;
    return Object.freeze([...ARTWORK_ORG_ONBOARDING_PURPOSES, ...missing]);
}
